/**
 * Track 2 (2026-08-17) — per-client Provisioning tab inside Clients
 * Information (ClientOverviewPage.js). Distinct from the "Tenant
 * Management (eBA)" tab: that tab owns legacy eBA1 `enabled_modules` /
 * `user_license_limit` on `clients`; this tab owns the EBA 2.0 control
 * plane (`eba2_tenants` / `eba2_tenant_environments` / licence and
 * module/function entitlements) — the two are never mixed.
 *
 * Buyer/Admin and Supplier Portal URLs and the SQL Server database name
 * are DERIVED from Company Code server-side (backend/eba2_provisioning_urls.py)
 * and shown read-only here — never independently typed, so neither can
 * drift out of sync with Company Code. See
 * sprint-15-eba-client-tenant-entitlements-control-plane.md Section R.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Globe, Plus, CheckCircle, XCircle, FloppyDisk, PencilSimple, X, Lock } from "@phosphor-icons/react";

const card = "border border-[#E5E5E5] p-5 bg-[#FAFAFA] mb-4";
const label = "text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]";
const sectionTitle = "text-sm font-bold uppercase tracking-[0.1em] text-[#0A0A0A] mb-3";

const SYSTEM_OPTIONS = [
  { code: "EBA1_LEGACY", label: "EBA 1.0 Legacy" },
  { code: "EBA2_AI", label: "EBA 2.0 AI" },
];
const SYSTEM_TYPE_OPTIONS = [
  { code: "STANDARD", label: "Standard System" },
  { code: "CUSTOM", label: "Custom System" },
];
const systemLabel = (code) => SYSTEM_OPTIONS.find((o) => o.code === code)?.label || code || "—";
const systemTypeLabel = (code) => SYSTEM_TYPE_OPTIONS.find((o) => o.code === code)?.label || code || "—";

/**
 * Client-context integrity (2026-08-18 security review). Every tenant-anchored
 * EBA2 endpoint now REQUIRES `client_id`; the backend rejects a request without
 * it rather than falling back to an unscoped operation. This helper is the only
 * place the parameter is attached, so no call site can quietly omit it, and it
 * throws rather than issuing a request when the Client context is somehow
 * absent — an unscoped request must never be the fallback behavior.
 */
function clientScope(clientId) {
  if (!clientId) {
    throw new Error("Missing client context — refusing to send an unscoped EBA provisioning request.");
  }
  return { params: { client_id: clientId } };
}

// Track 2 Standard+Custom ruling — display-only mirror of
// backend/eba2_provisioning_urls.py, used purely to preview what a
// pending System Type change would produce before the employee confirms
// it (Section 20). The backend remains the sole authoritative source;
// this never writes anything and re-derives nothing server-side.
const EYVO_PUBLIC_DOMAIN = "eyvo.ai";
const STANDARD_BUYER_HOSTNAME = EYVO_PUBLIC_DOMAIN;
const STANDARD_SUPPLIER_HOSTNAME = `supplierportal.${EYVO_PUBLIC_DOMAIN}`;
function previewHostnames(systemTypeCode, companyCode) {
  if (systemTypeCode === "CUSTOM") {
    if (!companyCode) return { buyer: null, supplier: null };
    return { buyer: `${companyCode}.${EYVO_PUBLIC_DOMAIN}`, supplier: `supplierportal.${companyCode}.${EYVO_PUBLIC_DOMAIN}` };
  }
  return { buyer: STANDARD_BUYER_HOSTNAME, supplier: STANDARD_SUPPLIER_HOSTNAME };
}

function Field({ label: l, value }) {
  return (
    <div className="flex items-start gap-2 py-0.5">
      <div className={`${label} w-[170px] flex-shrink-0 py-1`}>{l}</div>
      <div className="text-sm py-1">{value ?? <span className="text-[#A3A3A3] italic">—</span>}</div>
    </div>
  );
}

function ProvisionForm({ clientId, onCreated }) {
  const [tenantCode, setTenantCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [systemCode, setSystemCode] = useState("EBA2_AI");
  const [systemTypeCode, setSystemTypeCode] = useState("STANDARD");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!tenantCode || !displayName) {
      toast.error("Tenant code and display name are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/eba2/tenants", {
        client_id: clientId, tenant_code: tenantCode, display_name: displayName,
        entry_date: entryDate || undefined,
        system_code: systemCode, system_type_code: systemTypeCode,
      });
      toast.success("EBA 2.0 tenant provisioned");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to provision tenant");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="provisioning-empty-state">
      <div className={sectionTitle}>Not Provisioned</div>
      <p className="text-xs text-[#525252] mb-4">
        This client has no EBA 2.0 tenant yet. Provisioning creates the control-plane
        record Pulse uses to derive the Buyer/Admin URL, Supplier Portal URL, and database
        name — nothing is created in Azure or on the SQL Server VM by this action.
      </p>
      <div className="grid grid-cols-2 gap-3 max-w-xl">
        <div>
          <Label className={label}>Tenant Code</Label>
          <Input value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} className="rounded-none" data-testid="provisioning-tenant-code" />
        </div>
        <div>
          <Label className={label}>Display Name</Label>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="rounded-none" data-testid="provisioning-display-name" />
        </div>
        <div>
          <Label className={label}>Entry Date</Label>
          <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} className="rounded-none" />
        </div>
        <div>
          <Label className={label}>System</Label>
          <select className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white rounded-none" value={systemCode} onChange={(e) => setSystemCode(e.target.value)}>
            {SYSTEM_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <Label className={label}>System Type</Label>
          <select className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white rounded-none" value={systemTypeCode} onChange={(e) => setSystemTypeCode(e.target.value)}>
            {SYSTEM_TYPE_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
          </select>
        </div>
      </div>
      <Button onClick={submit} disabled={saving} className="mt-4 bg-[#0055FF] hover:bg-[#0040CC] text-white rounded-none" data-testid="provisioning-submit-btn">
        <Plus size={14} className="mr-1.5" /> Provision
      </Button>
    </div>
  );
}

function AddEnvironmentForm({ clientId, tenantId, isFirstEnvironment, onAdded, onCancel }) {
  const [environmentName, setEnvironmentName] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [initialAdminEmail, setInitialAdminEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null); // { suggested, available } | null
  const [codeTouched, setCodeTouched] = useState(false);

  // Track 2 Standard+Custom ruling, Section 3: suggest a Company Code from
  // the client's name only the first time this client is being
  // provisioned. A suggestion only — the field stays fully editable and
  // the employee may overtype it before submitting.
  useEffect(() => {
    if (!isFirstEnvironment) return;
    let cancelled = false;
    api.get(`/eba2/clients/${clientId}/company-code-suggestion`).then(({ data }) => {
      if (cancelled || !data.suggested) return;
      setSuggestion(data);
      if (!codeTouched) setCompanyCode(data.suggested);
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isFirstEnvironment]);

  const submit = async () => {
    if (!environmentName) {
      toast.error("Environment name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/eba2/tenants/${tenantId}/environments`, {
        environment_name: environmentName,
        company_code: companyCode || undefined,
        initial_admin_email: initialAdminEmail || undefined,
      }, clientScope(clientId));
      toast.success("Environment created");
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create environment");
    } finally {
      setSaving(false);
    }
  };

  const suggestionIsCurrentValue = suggestion?.suggested && suggestion.suggested === companyCode;

  return (
    <div className="border border-[#E5E5E5] p-3 mb-3 space-y-2 bg-white">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className={label}>Environment (e.g. dev, uat, prod)</div>
          <Input value={environmentName} onChange={(e) => setEnvironmentName(e.target.value)} className="rounded-none" />
        </div>
        <div>
          <div className={label}>Company Code</div>
          <Input
            value={companyCode}
            onChange={(e) => { setCodeTouched(true); setCompanyCode(e.target.value); }}
            placeholder="e.g. acme"
            className="rounded-none"
          />
          {suggestionIsCurrentValue && (
            <p className="text-[10px] text-[#A3A3A3] mt-0.5">
              Suggested from client name — you may change it before provisioning.
            </p>
          )}
          {suggestionIsCurrentValue && suggestion.available === false && (
            <p className="text-[10px] text-[#B42318] mt-0.5">
              This suggested code is already taken — please choose a different Company Code.
            </p>
          )}
        </div>
        <div>
          <div className={label}>Initial Admin Email</div>
          <Input value={initialAdminEmail} onChange={(e) => setInitialAdminEmail(e.target.value)} className="rounded-none" />
        </div>
      </div>
      <p className="text-[10px] text-[#A3A3A3]">
        Buyer/Admin URL, Supplier Portal URL, and the SQL Server database name are derived
        from Company Code and System Type automatically — they are not typed here. Company
        Code becomes read-only once this environment is first successfully provisioned.
      </p>
      <div className="flex gap-2">
        <Button size="sm" onClick={submit} disabled={saving}>Create</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

export default function ClientProvisioningTab({ clientId, isAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddEnv, setShowAddEnv] = useState(false);
  const [editingTenant, setEditingTenant] = useState(false);
  const [tenantForm, setTenantForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/eba2/clients/${clientId}/provisioning`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load provisioning data");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);

  const toggleEnvironmentActive = async (env) => {
    try {
      await api.put(`/eba2/tenants/${data.tenant.id}/environments/${env.id}`, { active: !env.active },
        clientScope(clientId));
      toast.success(env.active ? "Environment deactivated" : "Environment activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update environment");
    }
  };

  const saveTenant = async () => {
    try {
      await api.put(`/eba2/tenants/${data.tenant.id}`, {
        display_name: tenantForm.display_name,
        status: tenantForm.status,
        system_type_code: tenantForm.system_type_code,
      }, clientScope(clientId));
      toast.success("Provisioning updated");
      setEditingTenant(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update provisioning");
    }
  };

  // Client-context integrity: this tab is only ever reachable from inside a
  // Client record, so an absent clientId means something upstream is wrong.
  // Fail visibly rather than rendering controls whose requests would be
  // rejected server-side (or, worse, be sent unscoped).
  if (!clientId) {
    return (
      <div className={card} data-testid="provisioning-missing-client-context">
        <div className={sectionTitle}>Client context unavailable</div>
        <p className="text-xs text-[#525252]">
          EBA provisioning must be opened from a specific client record. Return to Clients and
          select the client you want to manage.
        </p>
      </div>
    );
  }

  if (loading) return <div className="text-sm text-[#A3A3A3]">Loading provisioning…</div>;
  if (!data) return null;

  if (!data.tenant) {
    return isAdmin
      ? <ProvisionForm clientId={clientId} onCreated={load} />
      : <div className={card}>Not provisioned.</div>;
  }

  const { tenant, environments, licences, modules, functions, client } = data;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe size={18} />
          <h2 className="text-lg font-black tracking-tight">EBA 2.0 Provisioning</h2>
        </div>
        {isAdmin && !editingTenant && (
          <Button size="sm" variant="outline" onClick={() => { setTenantForm(tenant); setEditingTenant(true); }} data-testid="update-provisioning-btn">
            <PencilSimple size={14} className="mr-1.5" /> Update Provisioning
          </Button>
        )}
      </div>

      {editingTenant && (
        <div className={card}>
          <div className={sectionTitle}>Update Provisioning</div>
          <div className="grid grid-cols-3 gap-3 max-w-2xl">
            <div>
              <div className={label}>Display Name</div>
              <Input value={tenantForm.display_name || ""} onChange={(e) => setTenantForm({ ...tenantForm, display_name: e.target.value })} className="rounded-none" />
            </div>
            <div>
              <div className={label}>Status</div>
              <select className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white rounded-none" value={tenantForm.status} onChange={(e) => setTenantForm({ ...tenantForm, status: e.target.value })}>
                {["draft", "active", "suspended", "decommissioned"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <div className={label}>System Type</div>
              <select className="w-full h-9 px-3 text-sm border border-[#E5E5E5] bg-white rounded-none" value={tenantForm.system_type_code} onChange={(e) => setTenantForm({ ...tenantForm, system_type_code: e.target.value })}>
                {SYSTEM_TYPE_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {tenantForm.system_type_code !== tenant.system_type_code && (environments || []).length > 0 && (
            <div className="mt-3 border border-[#FDBA74] bg-[#FFF7ED] p-3 max-w-2xl" data-testid="system-type-change-preview">
              <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#9A3412] mb-1.5">
                System Type change preview — {systemTypeLabel(tenant.system_type_code)} → {systemTypeLabel(tenantForm.system_type_code)}
              </div>
              <p className="text-[10px] text-[#9A3412] mb-2">
                Company Code, Tenant ID, database, users, and business data are unaffected —
                only the Buyer/Admin and Supplier Portal URLs below will change for every environment.
              </p>
              <table className="w-full text-xs">
                <tbody>
                  {environments.map((e) => {
                    const before = { buyer: e.hostname, supplier: e.supplier_hostname };
                    const after = previewHostnames(tenantForm.system_type_code, e.company_code);
                    return (
                      <tr key={e.id} className="border-t border-[#FDBA74]/50">
                        <td className="py-1 pr-3 font-mono">{e.environment_name}</td>
                        <td className="py-1 pr-2 font-mono text-[#9A3412]/70 line-through">{before.buyer ? `https://${before.buyer}` : "—"}</td>
                        <td className="py-1 font-mono font-bold text-[#9A3412]">{after.buyer ? `https://${after.buyer}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={saveTenant} data-testid="save-provisioning-btn"><FloppyDisk size={14} className="mr-1.5" /> Save</Button>
            <Button size="sm" variant="outline" onClick={() => setEditingTenant(false)}><X size={14} className="mr-1.5" /> Cancel</Button>
          </div>
        </div>
      )}

      {/* CLIENT */}
      <div className={card}>
        <div className={sectionTitle}>Client</div>
        <div className="grid grid-cols-2 gap-x-8">
          <div>
            <Field label="Client Name" value={client.name} />
            <Field label="Company Code" value={client.company_code} />
            <Field label="System" value={systemLabel(tenant.system_code)} />
            <Field label="System Type" value={systemTypeLabel(tenant.system_type_code)} />
          </div>
          <div>
            <Field label="Active Status" value={tenant.status} />
            <Field label="Entry Date" value={tenant.entry_date || "Unknown"} />
            <Field label="Tenant Code" value={tenant.tenant_code} />
          </div>
        </div>
      </div>

      {/* LICENSING */}
      <div className={card}>
        <div className={sectionTitle}>Licensing</div>
        <div className="flex gap-8">
          <div>
            <div className={label}>Main User</div>
            <div className="text-lg font-bold" data-testid="licence-main-user">{licences?.MAIN_USER ?? 0}</div>
          </div>
          <div>
            <div className={label}>Requester</div>
            <div className="text-lg font-bold" data-testid="licence-requester">{licences?.REQUESTER ?? 0}</div>
          </div>
          <div>
            <div className={label}>Derived Total</div>
            <div className="text-lg font-bold">{licences?.total ?? 0}</div>
          </div>
        </div>
        <p className="text-[10px] text-[#A3A3A3] mt-2">
          Administrators are licence-exempt (unlimited, never counted). There is no separate
          Approver licence — Main Users and permitted Requesters may both be approvers.
        </p>
      </div>

      {/* MODULES / FUNCTIONS */}
      <div className="grid grid-cols-2 gap-4">
        <div className={card}>
          <div className={sectionTitle}>Modules</div>
          {(modules || []).length === 0 ? (
            <div className="text-sm text-[#A3A3A3]">No modules enabled.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {modules.map((m) => (
                <span key={m.code} className="text-xs font-bold px-2 py-1 bg-[#F0FDF4] border border-[#16A34A] text-[#16A34A]">{m.display_name || m.code}</span>
              ))}
            </div>
          )}
        </div>
        <div className={card}>
          <div className={sectionTitle}>Functions</div>
          {(functions || []).length === 0 ? (
            <div className="text-sm text-[#A3A3A3]">No functions enabled.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {functions.map((f) => (
                <span key={f.code} className="text-xs font-bold px-2 py-1 bg-[#F0FDF4] border border-[#16A34A] text-[#16A34A]">{f.display_name || f.code}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ENVIRONMENT */}
      <div className={card}>
        <div className="flex items-center justify-between mb-3">
          <div className={sectionTitle}>Environments</div>
          {isAdmin && !showAddEnv && (
            <Button size="sm" onClick={() => setShowAddEnv(true)} data-testid="add-environment-btn">
              <Plus size={14} className="mr-1" /> Add Environment
            </Button>
          )}
        </div>
        {showAddEnv && (
          <AddEnvironmentForm
            clientId={clientId}
            tenantId={tenant.id}
            isFirstEnvironment={(environments || []).length === 0}
            onAdded={() => { setShowAddEnv(false); load(); }}
            onCancel={() => setShowAddEnv(false)}
          />
        )}
        {(environments || []).length === 0 ? (
          <div className="text-sm text-[#A3A3A3]">No environments yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E5E5E5]">
                  <th className="py-1">Environment</th>
                  <th className="py-1">Company Code</th>
                  <th className="py-1">Buyer / Admin URL</th>
                  <th className="py-1">Supplier Portal URL</th>
                  <th className="py-1">Database</th>
                  <th className="py-1">Auth Mode</th>
                  <th className="py-1">Status</th>
                  {isAdmin && <th className="py-1"></th>}
                </tr>
              </thead>
              <tbody>
                {environments.map((e) => (
                  <tr key={e.id} className="border-b border-[#F5F5F5]" data-testid={`environment-row-${e.environment_name}`}>
                    <td className="py-1.5 font-mono">{e.environment_name}</td>
                    <td className="py-1.5 font-mono text-xs">
                      <span className="inline-flex items-center gap-1">
                        {e.company_code || "—"}
                        {e.company_code_locked && (
                          <Lock size={11} className="text-[#A3A3A3]" title="Locked after first provisioning" />
                        )}
                      </span>
                    </td>
                    <td className="py-1.5 font-mono text-xs">{e.hostname ? `https://${e.hostname}` : "—"}</td>
                    <td className="py-1.5 font-mono text-xs">{e.supplier_hostname ? `https://${e.supplier_hostname}` : "—"}</td>
                    <td className="py-1.5 font-mono text-xs">{e.db_locator || "—"}</td>
                    <td className="py-1.5 text-xs" data-testid={`auth-mode-${e.environment_name}`}>{e.auth_mode || "—"}</td>
                    <td className="py-1.5">
                      {e.active
                        ? <span className="text-[#0A7A3D] font-medium flex items-center gap-1"><CheckCircle size={14} weight="fill" /> Active</span>
                        : <span className="text-[#B42318] font-medium flex items-center gap-1"><XCircle size={14} /> Inactive</span>}
                      <div className="text-[10px] text-[#A3A3A3]">{e.lifecycle_status} · {e.provisioning_status}</div>
                    </td>
                    {isAdmin && (
                      <td className="py-1.5 text-right">
                        <Button size="sm" variant="outline" onClick={() => toggleEnvironmentActive(e)} data-testid={`toggle-active-${e.environment_name}`}>
                          {e.active ? "Deactivate" : "Activate"}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
